/**
 * CollectionCopilotDrawer - Thin Container for UnifiedCoPilot
 * 
 * This is a CONTAINER ONLY - all co-pilot logic lives in UnifiedCoPilot.
 * 
 * Features:
 * - Resizable width with localStorage memory
 * - Collapse to 44px vertical bar
 * - Animated gradient border
 * - Renders UnifiedCoPilot inside
 * - Feature flag aware - hides when copilot is disabled
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useFeatureFlags, COPILOT_CONTEXT_FLAGS } from '../../hooks/useFeatureFlags';
import UnifiedCoPilot from './UnifiedCoPilot';

// Drawer size constraints
const MIN_WIDTH = 485;
const MAX_WIDTH = 800;

// Default personality icons based on context
const PERSONALITY_MAP = {
  features: { icon: '✨', color: 'text-purple-500' },
  epics: { icon: '🎯', color: 'text-blue-500' },
  goals: { icon: '🏆', color: 'text-amber-500' },
  initiatives: { icon: '🚀', color: 'text-green-500' },
  personas: { icon: '👥', color: 'text-pink-500' },
  releases: { icon: '📅', color: 'text-cyan-500' },
  requests: { icon: '📥', color: 'text-orange-500' },
  ideation: { icon: '💡', color: 'text-yellow-500' },
  kanban: { icon: '📋', color: 'text-indigo-500' },
  strategy: { icon: '🧭', color: 'text-red-500' },
  market: { icon: '📈', color: 'text-emerald-500' },
  help: { icon: '📖', color: 'text-indigo-500' },
};

/**
 * CollectionCopilotDrawer - Container for collection-level co-pilots
 */
const CollectionCopilotDrawer = ({
  // Required
  context,              // 'features', 'epics', 'requests', 'strategy', etc.
  contextId,            // productId
  
  // UI Configuration
  title: propTitle,
  icon: propIcon,
  
  // Behavior
  enableResize = true,
  enableCollapse = true,
  defaultWidth = 485,
  storageKeyPrefix = 'collectionCopilot',
  
  // Callbacks
  onRefresh,
  onSplitItem,          // Handler for SPLIT_ITEM actions (e.g., open SplitFeatureModal)
  
  // Data for UnifiedCoPilot
  contextData = {},
  relatedData = {},
  injectedContext,      // Optional pre-formatted context string to inject into LLM prompt
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Check feature flag for this copilot
  const { isEnabled } = useFeatureFlags();
  const copilotFeatureFlag = COPILOT_CONTEXT_FLAGS[context];
  const isCopilotEnabled = !copilotFeatureFlag || isEnabled(copilotFeatureFlag);
  
  // Storage keys
  const storageKey = `${storageKeyPrefix}_${contextId}_collapsed`;
  const widthStorageKey = `${storageKeyPrefix}_width`;
  
  // UI State - must be called unconditionally
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === 'true';
  });
  const [drawerWidth, setDrawerWidth] = useState(() => {
    const saved = localStorage.getItem(widthStorageKey);
    return saved ? parseInt(saved, 10) : defaultWidth;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Refs
  const drawerRef = useRef(null);
  const didDragRef = useRef(false);
  
  // Derived display values (compute even if disabled)
  const displayTitle = propTitle || `${context.charAt(0).toUpperCase()}${context.slice(1)} Assistant`;
  const displayIcon = propIcon || PERSONALITY_MAP[context]?.icon || '✨';
  
  // Persist collapsed state
  useEffect(() => {
    if (isCopilotEnabled) {
      localStorage.setItem(storageKey, isCollapsed.toString());
    }
  }, [isCollapsed, storageKey, isCopilotEnabled]);
  
  // Persist width
  useEffect(() => {
    if (isCopilotEnabled && drawerWidth && !isCollapsed && enableResize) {
      localStorage.setItem(widthStorageKey, drawerWidth.toString());
    }
  }, [drawerWidth, isCollapsed, enableResize, widthStorageKey, isCopilotEnabled]);
  
  // Handle resize drag
  const handleMouseDown = (e) => {
    if (!enableResize) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    didDragRef.current = false;
    
    const startX = e.clientX;
    const startWidth = drawerWidth;
    
    const handleMouseMove = (e) => {
      didDragRef.current = true;
      const delta = startX - e.clientX;
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
      setDrawerWidth(newWidth);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Handle handle click (toggle collapse)
  const handleHandleClick = (e) => {
    if (!enableCollapse) return;
    e.stopPropagation();
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    toggleDrawer();
  };
  
  // Toggle drawer
  const toggleDrawer = () => {
    setIsCollapsed(!isCollapsed);
  };
  
  // Handle updates from UnifiedCoPilot (for collection context, this is typically a no-op
  // since collection co-pilots usually perform actions via API, not form updates)
  const handleApplyUpdates = (updates) => {
    console.log('[CollectionCopilotDrawer] Apply updates:', updates);
    // For collections, updates are typically applied directly via API
    // The UnifiedCoPilot handles this internally for collection types
    if (onRefresh) {
      onRefresh();
    }
  };
  
  // If copilot is disabled via feature flag, don't render
  // NOTE: This must come AFTER all hooks to comply with React rules-of-hooks
  if (!isCopilotEnabled) {
    return null;
  }
  
  // Render collapsed state
  if (isCollapsed && enableCollapse) {
    return (
      <div
        data-drawer-id={storageKey}
        data-testid="copilot-drawer-collapsed"
        className={cn(
          "h-full w-[44px] flex flex-col items-center py-4 cursor-pointer border-l transition-all duration-300",
          colors.bgSecondary, colors.borderPrimary
        )}
        onClick={toggleDrawer}
      >
        {/* Animated gradient border */}
        <div
          className="absolute left-0 inset-y-0 w-[3px]"
          style={{
            background: 'linear-gradient(180deg, #1e3a8a, #3b82f6, #06b6d4, #e0f2fe, #8b5cf6, #3b82f6, #1e3a8a)',
            backgroundSize: '100% 300%',
            animation: 'colorChaseVertical 3s linear infinite',
          }}
        />
        
        {/* Icon */}
        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center mb-3", colors.bgActive)}>
          {typeof displayIcon === 'string' && displayIcon.length <= 2 ? (
            <span className="text-sm">{displayIcon}</span>
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
        </div>
        
        {/* Vertical title */}
        <div
          className={cn("text-xs font-medium whitespace-nowrap", colors.textMuted)}
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
        >
          {displayTitle}
        </div>
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="mt-auto mb-2">
            <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
          </div>
        )}
        
        {/* Expand arrow */}
        <ChevronRight className={cn("w-4 h-4 mt-auto transform rotate-180", colors.textMuted)} />
      </div>
    );
  }
  
  // Render expanded state
  return (
    <div
      ref={drawerRef}
      data-drawer-id={storageKey}
      data-testid="copilot-drawer-expanded"
      className={cn(
        "h-full flex flex-col border-l relative transition-all duration-300",
        colors.bgSecondary, colors.borderPrimary
      )}
      style={{ width: enableResize ? drawerWidth : defaultWidth, minWidth: MIN_WIDTH }}
    >
      {/* Animated gradient border */}
      <div
        className="absolute left-0 inset-y-0 w-[3px] z-10"
        style={{
          background: 'linear-gradient(180deg, #1e3a8a, #3b82f6, #06b6d4, #e0f2fe, #8b5cf6, #3b82f6, #1e3a8a)',
          backgroundSize: '100% 300%',
          animation: 'colorChaseVertical 3s linear infinite',
        }}
      />
      
      {/* Drag handle */}
      {enableResize && (
        <div
          className={cn(
            "absolute left-0 inset-y-0 w-2 cursor-ew-resize z-20 hover:bg-blue-500/20",
            isDragging && "bg-blue-500/30"
          )}
          onMouseDown={handleMouseDown}
          onClick={handleHandleClick}
        />
      )}
      
      {/* UnifiedCoPilot - handles ALL co-pilot functionality including header */}
      <div className="flex-1 overflow-hidden">
        <UnifiedCoPilot
          contextType={context}
          contextId={contextId}
          productId={contextData?.product_id || contextId}
          contextData={contextData}
          relatedData={relatedData}
          injectedContext={injectedContext}
          onApplyUpdates={handleApplyUpdates}
          onRefresh={onRefresh}
          onSplitItem={onSplitItem}
          onCollapse={enableCollapse ? toggleDrawer : undefined}
          variant="collection"
          className="h-full w-full"
        />
      </div>
      
      {/* CSS for animation */}
      <style>{`
        @keyframes colorChaseVertical {
          0% { background-position: 0% 0%; }
          100% { background-position: 0% 100%; }
        }
      `}</style>
    </div>
  );
};

export default CollectionCopilotDrawer;
